import { useState, useCallback, useEffect, useRef } from "react";
import { useMatch, useNavigate } from "react-router-dom";

import { useModalContext } from "../../../../../../../_metronic/elements/Modal";
import { openAlert } from "../../../../../../../_metronic/elements/NotificationAlert";
import { Loading } from "../../../../../../../_metronic/elements/Loading";
import { useErrorHandler } from "../../../../../../../_metronic/helpers/ErrorHandler";
import { useViewLandContext } from "../ViewLandContext";
import { useViewImageContext } from "../Images/ViewImageContext";
import { useMapLegendContext } from '../Helpers/MapLegend';
import { useViewAmbientContext } from "../Ambients/AmbientsContext";
import { format } from "date-fns";
import ImageAmbientForm from "./ImageAmbientForm";
import * as GisDataAdapter from "../../../services/GisDataAdapter";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../../../../../../shared/responsive";
import { getImageQuality, isImageAmbientSourceType } from "../Images/imageTypeUtils";
import { PLANET_HD_V1_ENABLED } from "../../../../../../config/featureFlags";
import {
  buildImageAmbientHdPayload,
  clearImageAmbientHdRequest,
  getImageAmbientHdBasis,
  getOrCreateImageAmbientHdRequest,
} from "./imageAmbientHdModel";

const clsPresc = {
  0: 'mbp',
  1: 'bp',
  2: 'pp',
  3: 'ap',
  4: 'map',
  5: 'five',
  6: 'six'
};

const buildEmptyImageAmbientSummary = () => ({
  map: { ha: 0 },
  ap: { ha: 0 },
  pp: { ha: 0 },
  bp: { ha: 0 },
  mbp: { ha: 0 },
  five: { ha: 0 },
  six: { ha: 0 }
});

const ModalHeader = ({ isHdCreation, setNextStep, isMobile }) => {
  const { t } = useTranslation();
  return (
    <>
      <div className={`modal-header prescription-modal__header ${isMobile ? "prescription-modal__header--mobile" : ""}`.trim()}>
        <button
          type="button"
          className="btn btn-light prescription-modal__back-button"
          onClick={(e) => {
            e.preventDefault();
            setNextStep(false);
          }}
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" className="bi bi-arrow-left" viewBox="0 0 16 16">
            <path fillRule="evenodd" d="M15 8a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 0 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 0 1 .708.708L2.707 7.5H14.5A.5.5 0 0 1 15 8z"/>
          </svg>
        </button>
        <div className="prescription-modal__title-group">
          <h5 className="modal-title">{t("img_ambient:creation.title")}</h5>
          <p className="prescription-modal__description">
            {isHdCreation
              ? t("img_ambient:creation.description_hd")
              : t("img_ambient:creation.description")}
          </p>
        </div>
      </div>
    </>
  );
};

const ModalFooter = ({ downloading, isHdCreation, isMobile, canSubmit = true }) => {
  const modal = useModalContext();
  const { t } = useTranslation();

  return (
    <div className={`modal-footer prescription-modal__footer ${isMobile ? "prescription-modal__footer--mobile" : ""}`.trim()}>
      <button disabled={downloading} type="button" className="btn btn-light prescription-modal__footer-button" onClick={() => modal.hideModal()}>
        {t("img_ambient:creation.buttons.cancel")}
      </button>
      <button disabled={downloading || !canSubmit} type="submit" className="btn btn-primary prescription-modal__footer-button">
        {isHdCreation
          ? t("img_ambient:creation.buttons.create_hd_job")
          : t("img_ambient:creation.buttons.save_ambientation")}
      </button>
    </div>
  )
}

const ImageAmbientDialogClasses = ({ ambient, numClasses, setNextStep }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [img_ambient, setImageAmbient] = useState({});
  const [geojson_preview, setGeojsonPreview] = useState({});
  const [loadError, setLoadError] = useState("");
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isAmbientActive = useMatch('/gis/field/view/:field_id/land/view/:land_id/ambients');
  const viewLandContext = useViewLandContext();
  const viewImageContext = useViewImageContext();
  const mapLegendContext = useMapLegendContext();
  const {
    buildQueuedImageAmbientData,
    loadImageAmbientData,
    loadImageAmbientHdData,
    setImageAmbientData,
    setImageAmbientHdData,
    setSelectedElement,
  } = useViewAmbientContext();
  const navigate = useNavigate();
  const modal = useModalContext();
  const lastLoadKey = useRef(null);
  const landId = viewLandContext.land?.land_id;
  const activeImageLand = landId ? viewImageContext.getLand(landId) : null;
  const activeImageDateKey = activeImageLand?.selectedDate
    ? format(activeImageLand.selectedDate, 'yyyy-MM-dd')
    : "no-date";
  const selectedLayerDate = activeImageLand?.selectedDate
    ? activeImageDateKey
    : ambient?.params?.layer_date;
  const activeImageTypeKey = activeImageLand?.imageType || "no-type";
  const isHdCreationFlow = Boolean(
    PLANET_HD_V1_ENABLED &&
    (!isAmbientActive || !ambient) &&
    getImageQuality(activeImageLand?.imageType) === "hd"
  );
  const savedAmbientKey = ambient
    ? [
      ambient.ambient_id || "no-id",
      ambient.status ?? "no-status",
      ambient.params?.breaks ?? "no-breaks",
      ambient.geojson?.features?.length ?? 0,
    ].join(":")
    : "no-ambient";
  const loadKey = [
    isAmbientActive && ambient ? "saved" : "image",
    landId || "no-land",
    activeImageTypeKey,
    activeImageDateKey,
    activeImageLand?.selectedAcquisitionId || "no-acquisition",
    numClasses,
    savedAmbientKey,
  ].join("|");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    const img_amb = buildEmptyImageAmbientSummary();

    try {
      if (!isAmbientActive || !ambient) {
        const imageLand = viewImageContext.getLand(viewLandContext.land.land_id);

        if (imageLand == null || imageLand.selectedDate == null) {
          setLoadError(t("img_ambient:creation.errors.missing_active_image"));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        if (!isImageAmbientSourceType(imageLand.imageType)) {
          setLoadError(t("images:ambient_requires_evi", {
            defaultValue: "Seleccioná EVI para crear una ambientación desde imagen.",
          }));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        const imageQuality = getImageQuality(imageLand['imageType']);
        const imageDate = format(imageLand['selectedDate'], 'yyyy-MM-dd');
        const canonicalImageAmbientHd = PLANET_HD_V1_ENABLED && imageQuality === "hd";
        const acquisitionId = imageLand.selectedAcquisitionId;

        if (imageQuality === "hd" && !canonicalImageAmbientHd) {
          setLoadError(t("img_ambient:creation.errors.hd_confirmation_required"));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        if (
          canonicalImageAmbientHd &&
          (!acquisitionId || imageLand.selectedAcquisitionStatus !== "ready")
        ) {
          setLoadError(t("img_ambient:creation.errors.hd_quality_not_eligible"));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        if (canonicalImageAmbientHd) {
          const authorized = await viewImageContext.requestHdAccess({
            land: viewLandContext.land,
            operation: "image_ambient_hd",
            context: {
              image_type: imageLand['imageType'],
              date: imageDate,
              num_classes: numClasses,
            },
            onConfirmed: (access) => access,
          });

          if (!authorized) {
            setLoadError(t("img_ambient:creation.errors.hd_confirmation_required"));
            setGeojsonPreview({ type: "FeatureCollection", features: [] });
            setImageAmbient(img_amb);
            return;
          }

          // HD classification is intentionally not previewed client-side. The
          // billed image-ambient job is the only endpoint allowed to produce
          // reusable HD classes/GeoJSON.
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        const classificationPayload = {
          layer: 'evi',
          date: imageDate,
          num_classes: numClasses,
          quality: imageQuality,
        };
        const res = await errorHandler(
          GisDataAdapter.classifyLandPrescription(
            viewLandContext.land.land_id,
            classificationPayload
          )
        );

        if (!res.ok) {
          setLoadError(t("img_ambient:creation.errors.preview_failed"));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        const { classes, preview } = res.data;

        if (!classes.length || !Array.isArray(preview?.features) || preview.features.length < 1) {
          setLoadError(t("img_ambient:creation.errors.invalid_classification"));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        classes.forEach(({ cls, sup_ha, ha }) => {
          const key = clsPresc[cls];
          if (!key) {
            return;
          }

          img_amb[key]['ha'] += sup_ha ?? ha ?? 0;
          img_amb[key]['cls'] = cls;
        });

        setGeojsonPreview(preview);
      } else {
        if (!ambient?.geojson?.features?.length) {
          setLoadError(t("img_ambient:creation.errors.missing_saved_classes"));
          setGeojsonPreview({ type: "FeatureCollection", features: [] });
          setImageAmbient(img_amb);
          return;
        }

        ambient.geojson.features.forEach(e => {
          img_amb[clsPresc[e.properties.cls]]['ha'] += e.properties.sup_ha;
          img_amb[clsPresc[e.properties.cls]]['cls'] = e.properties.cls;
        });

        setGeojsonPreview(ambient.geojson);
      }

      setImageAmbient(img_amb);
    } finally {
      setLoading(false);
    }
  }, [ambient, errorHandler, isAmbientActive, numClasses, t, viewImageContext, viewLandContext.land]);

  useEffect(() => {
    if (lastLoadKey.current === loadKey) {
      return;
    }

    lastLoadKey.current = loadKey;
    load();
  }, [load, loadKey]);

  const handleGeneration = async (e) => {
    e.preventDefault();
    setDownloading(true);
    

    let res;
   
    const imageLand = viewImageContext.getLand(viewLandContext.land.land_id);
    // Re-read the current selection: it may have changed after preview loaded.
    if (!isImageAmbientSourceType(imageLand?.imageType)) {
      openAlert(
        "danger",
        t("img_ambient:creation.errors.title"),
        t("images:ambient_requires_evi", {
          defaultValue: "Seleccioná EVI para crear una ambientación desde imagen.",
        })
      );
      setDownloading(false);
      return;
    }
    const imageQuality = getImageQuality(imageLand['imageType']);
    const canonicalImageAmbientHd = PLANET_HD_V1_ENABLED && imageQuality === "hd";
    if (imageQuality === "hd" && !canonicalImageAmbientHd) {
      openAlert(
        "danger",
        t("img_ambient:creation.errors.title"),
        t("img_ambient:creation.errors.hd_confirmation_required")
      );
      setDownloading(false);
      return;
    }
    if (
      canonicalImageAmbientHd &&
      (!imageLand.selectedAcquisitionId || imageLand.selectedAcquisitionStatus !== "ready")
    ) {
      openAlert(
        "danger",
        t("img_ambient:creation.errors.title"),
        t("img_ambient:creation.errors.hd_quality_not_eligible", {
          defaultValue: "La adquisición HD seleccionada puede visualizarse, pero no es apta para ambientar.",
        })
      );
      setDownloading(false);
      return;
    }
    const basis = getImageAmbientHdBasis(imageLand.imageType);
    let imageAmbientRequest = null;
    const totalRequest = canonicalImageAmbientHd
      ? (() => {
          imageAmbientRequest = getOrCreateImageAmbientHdRequest({
            acquisitionId: imageLand.selectedAcquisitionId,
            basis,
            landId: viewLandContext.land.land_id,
            numClasses,
          });
          return buildImageAmbientHdPayload({
            acquisitionId: imageLand.selectedAcquisitionId,
            basis,
            idempotencyKey: imageAmbientRequest.requestId,
            numClasses,
          });
        })()
      : {
          layer: 'evi',
          layer_date: format(imageLand['selectedDate'], 'yyyy-MM-dd'),
          num_classes: numClasses,
          quality: imageQuality,
          geojson: geojson_preview
        };
    res = await errorHandler(
      canonicalImageAmbientHd
        ? GisDataAdapter.createLandImageAmbientHd(viewLandContext.land.land_id, totalRequest)
        : GisDataAdapter.createLandImageAmbient(viewLandContext.land.land_id, totalRequest)
    );
  

    if (res?.ok) {
      openAlert(
        'success',
        canonicalImageAmbientHd
          ? t("img_ambient:creation.success_hd.title")
          : t("img_ambient:creation.success.title"),
        canonicalImageAmbientHd
          ? t("img_ambient:creation.success_hd.message")
          : t("img_ambient:creation.success.message")
      );
      if (imageAmbientRequest?.storageKey) {
        clearImageAmbientHdRequest(imageAmbientRequest.storageKey);
      }
      const setAmbientData = canonicalImageAmbientHd
        ? setImageAmbientHdData
        : setImageAmbientData;
      setAmbientData({
        ...buildQueuedImageAmbientData({
          acquisitionId: imageLand.selectedAcquisitionId,
          ambientId: res.data?.ambient_id,
          basis,
          layer: totalRequest.layer || basis,
          layerDate: totalRequest.layer_date || format(imageLand['selectedDate'], 'yyyy-MM-dd'),
          numClasses,
          quality: imageQuality,
        }),
        ...(res.data || {}),
        status: res.data?.status ?? 0,
        progress: res.data?.progress ?? 0,
        editable: false,
      });
      modal.hideModal();
      mapLegendContext.deleteAll();
      viewImageContext.closeAllImages();
      setSelectedElement(canonicalImageAmbientHd ? "imageAmbientHd" : "imageAmbient");
      window.setTimeout(() => {
        const loader = canonicalImageAmbientHd
          ? loadImageAmbientHdData
          : loadImageAmbientData;
        loader().catch(() => {});
      }, 10000);
      
      navigate(`/gis/field/view/${viewLandContext.land.field.field_id}/land/view/${viewLandContext.land.land_id}/ambients`);


    }

    setDownloading(false);
  }


  if (loading) {
    return <div className="p-5 flex"><Loading/></div>;
  }

  if (img_ambient == null || Object.keys(img_ambient).length < 1) {
    return (<></>);
  }

  return (
    <>
      <form onSubmit={handleGeneration} className='form'>
        <ModalHeader
          isHdCreation={isHdCreationFlow}
          setNextStep={setNextStep}
          isMobile={isMobile}
        />
        <div className={`modal-body prescription-modal__body ${isMobile ? "prescription-modal__body--mobile" : "prescription-modal__body--image-ambient"}`.trim()}>
          {!loading && !loadError && isHdCreationFlow && (
            <div className="alert alert-info mb-0" role="status">
              <strong>{t("img_ambient:creation.hd_job.title")}</strong>
              <p className="mb-0 mt-2">
                {t("img_ambient:creation.hd_job.description", {
                  basis: getImageAmbientHdBasis(activeImageLand?.imageType).toUpperCase(),
                  classes: numClasses,
                })}
              </p>
            </div>
          )}
          {!loading && !loadError && !isHdCreationFlow && (
            <ImageAmbientForm
              numClasses={numClasses}
              geojson_preview={geojson_preview}
              layerDate={selectedLayerDate}
            />
          )}
          {!loading && loadError && (
            <div className="alert alert-warning mb-0" role="alert">
              {loadError}
            </div>
          )}
        </div>
        <ModalFooter
          downloading={downloading}
          isHdCreation={isHdCreationFlow}
          isMobile={isMobile}
          canSubmit={!loadError}
        />
      </form>
    </>
  );
};

export default ImageAmbientDialogClasses;
